import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, Link } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート

// --- 定数 ---
const API_URL = "https://salesbreaker.jp"; 
const ITEMS_PER_PAGE = 50;

// --- アイコンコンポーネント ---
const ChevronsUpDownIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 ml-1.5 opacity-60"><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5-5"/></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>;
const BrainCircuitIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0 0-3.952 19.424M12 2a10 10 0 0 1 3.952 19.424"/><path d="M12 2v4"/><path d="M12 22v-4"/><path d="M20 12h-4"/><path d="M4 12h4"/><path d="m17 17-1.1-1.1"/><path d="m7 7 1.1 1.1"/><path d="m7 17 1.1-1.1"/><path d="m17 7-1.1 1.1"/></svg>;
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const AlertCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;

// --- UIコンポーネント ---
const SummaryCard = ({ title, value, icon, colorClass }) => (
    <div className={`bg-white p-4 rounded-lg shadow-sm border-l-4 ${colorClass}`}>
        <div className="flex items-center">
            <div className="p-3 rounded-full bg-gray-100 mr-4">{icon}</div>
            <div>
                <p className="text-sm font-medium text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
        </div>
    </div>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    const createPageLinks = () => {
        const pageLinks = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pageLinks.push(i);
        } else {
            pageLinks.push(1);
            if (currentPage > 3) pageLinks.push('...');
            const startPage = Math.max(2, currentPage - 1);
            const endPage = Math.min(totalPages - 1, currentPage + 1);
            for (let i = startPage; i <= endPage; i++) pageLinks.push(i);
            if (currentPage < totalPages - 2) pageLinks.push('...');
            pageLinks.push(totalPages);
        }
        return pageLinks;
    };
    const pageLinks = createPageLinks();
    return (
        <nav className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div><p className="text-sm text-gray-700">全 {totalPages} ページ</p></div>
                <div>
                    <nav className="relative z-0 flex flex-wrap gap-1" aria-label="Pagination">
                        {pageLinks.map((page, index) =>
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
                            ) : (
                                <button key={page} onClick={() => onPageChange(page)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${currentPage === page ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>{page}</button>
                            )
                        )}
                    </nav>
                </div>
            </div>
        </nav>
    );
};

// ★★★ 新規追加: ゲーミフィケーション用コンポーネント ★★★
const RewardProgressCard = ({ privileges }) => {
    if (!privileges) return null;

    const { reviewPoints = 0, reviewPointsThreshold = 500 } = privileges;
    const progress = Math.min((reviewPoints / reviewPointsThreshold) * 100, 100);
    const isGoalAchieved = reviewPoints >= reviewPointsThreshold;
    const remainingPoints = reviewPointsThreshold - reviewPoints;

    return (
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-indigo-500 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">レビュー貢献状況</h3>
            <div className="space-y-2">
                {isGoalAchieved ? (
                    <div className="text-center py-4">
                        <p className="text-2xl font-bold text-green-600">🎉 ノルマ達成！</p>
                        <p className="text-gray-600 mt-2">プラン変更の特典が解放されました。ご協力ありがとうございます！</p>
                        <Link to="/account" className="mt-4 inline-block bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors">
                            アカウント管理へ
                        </Link>
                    </div>
                ) : (
                    <>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="font-medium text-gray-700">ノルマ達成まであと <span className="font-bold text-indigo-600">{remainingPoints}</span> 件！</span>
                                <span className="text-gray-500">{reviewPoints} / {reviewPointsThreshold}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div className="bg-indigo-600 h-4 rounded-full text-white text-xs flex items-center justify-center" style={{ width: `${progress}%` }}>
                                    {progress > 10 && `${progress.toFixed(0)}%`}
                                </div>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 text-center pt-2">1案件レビューするごとに1ポイント獲得できます。</p>
                    </>
                )}
            </div>
        </div>
    );
};

const FLOW_TYPE_LABELS = {
  confirm_page: '確認ページ型',
  multi_step: '多段階フォーム',
  js_two_step_hidden_confirm: 'JS確認ページ',
  direct_submit: '直接送信',
};

const REVIEW_STATUS_OPTIONS = [
  { value: 'all', label: 'すべてのステータス' },
  { value: 'SUCCESS', label: '成功' },
  { value: 'NEEDS_REVIEW', label: '要確認' },
  { value: 'LOGIC_FAILED', label: '価値ある失敗' },
  { value: 'PRE_SUBMIT_REVIEW', label: '送信前レビュー' },
  { value: 'WRONG_FORM_TYPE', label: '種別違い' },
  { value: 'SALES_REJECTED', label: '営業拒否' },
  { value: 'NO_FORM_FOUND', label: '対象外' },
  { value: 'DEAD_URL', label: 'URL無効' },
  { value: 'ANALYZING', label: '解析中' },
  { value: 'FAIL', label: '失敗' },
  { value: 'PENDING_APPROVAL', label: '承認待ち' },
  { value: '__NULL__', label: '未設定' },
];

const REVIEW_STATUS_LABELS = REVIEW_STATUS_OPTIONS.reduce((labels, option) => {
  labels[option.value] = option.label;
  return labels;
}, {});

const REVIEW_STATUS_STYLES = {
  SUCCESS: 'bg-green-100 text-green-800',
  NEEDS_REVIEW: 'bg-yellow-100 text-yellow-800',
  LOGIC_FAILED: 'bg-orange-100 text-orange-800',
  PRE_SUBMIT_REVIEW: 'bg-amber-100 text-amber-800',
  WRONG_FORM_TYPE: 'bg-purple-100 text-purple-800',
  SALES_REJECTED: 'bg-pink-100 text-pink-800',
  FAIL: 'bg-red-100 text-red-800',
  NO_FORM_FOUND: 'bg-gray-100 text-gray-800',
  DEAD_URL: 'bg-slate-100 text-slate-800',
  ANALYZING: 'bg-cyan-100 text-cyan-800',
  PENDING_APPROVAL: 'bg-blue-100 text-blue-800',
  __NULL__: 'bg-gray-50 text-gray-500',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  DEFAULT: 'bg-gray-100 text-gray-800'
};

function humanizeFlowType(flowType) {
  return FLOW_TYPE_LABELS[flowType] || flowType;
}

function humanizeReviewSurfaceSummary(text) {
  if (!text) return '';
  return String(text)
    .replaceAll('confirm_surface', '確認画面')
    .replaceAll('direct_submit_surface', '直接送信画面')
    .replaceAll('followup_input_surface', '追加入力画面')
    .replaceAll('sales_rejected_surface', '営業拒否')
    .replaceAll('wrong_form_type_surface', '種別違い')
    .replaceAll('no_followup_input_step', '追加入力待ち')
    .replaceAll('confirmation_stage_detected', '確認画面を検知')
    .replaceAll('direct_submit_ready_detected', '送信直前まで到達')
    .replaceAll('sendable=true', '送信可能寄り');
}


// --- メインコンポーネント ---
const ReviewDashboard = () => {
  const { user, userPrivileges } = useOutletContext(); // ★ 修正点: userPrivileges を context から取得
  const isClientView = user?.role === 'client_admin';
  
  const [activeTab, setActiveTab] = useState(isClientView ? 'client_review' : 'admin');
  
  const [reviewData, setReviewData] = useState({ items: [], pagination: {}, summary: {} });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({ status: 'all', reviewed: 'unreviewed', searchTerm: '' });
  const [clientApprovalFilters, setClientApprovalFilters] = useState({ status: 'pending_approval', searchTerm: '' });

  const [sortBy, setSortBy] = useState({ column: 'updated_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const endpoint = activeTab === 'admin' || activeTab === 'client_review'
      ? `${API_URL}/api/admin/review-queue`
      : `${API_URL}/api/admin/client-review-queue`;

    const currentFilters = activeTab === 'admin' || activeTab === 'client_review' ? filters : clientApprovalFilters;

    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        searchTerm: debouncedSearchTerm,
        sortBy: sortBy.column,
        sortDirection: sortBy.direction,
      });

      // ★ 修正点: クライアントビューの場合、clientIdを付与してデータを絞り込む
      if (isClientView) {
          params.append('clientId', user.client_id);
      }
      
      if (activeTab === 'admin' || activeTab === 'client_review') {
        params.append('statusFilter', currentFilters.status);
        params.append('reviewedFilter', currentFilters.reviewed);
      } else {
        params.append('status', currentFilters.status);
      }

      const res = await fetch(`${endpoint}?${params.toString()}`, {
        headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
      });
      if (!res.ok) throw new Error('レビュー案件の取得に失敗しました。');
      
      const data = await res.json();
      if (data.success) {
        setReviewData({ items: data.reviewItems || data.reviews || [], pagination: data.pagination || {}, summary: data.summary || {} });
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage, filters, clientApprovalFilters, debouncedSearchTerm, sortBy, isClientView, user?.client_id]);

  useEffect(() => {
    const currentSearchTerm = activeTab === 'admin' || activeTab === 'client_review' ? filters.searchTerm : clientApprovalFilters.searchTerm;
    const handler = setTimeout(() => { setDebouncedSearchTerm(currentSearchTerm); }, 500);
    return () => { clearTimeout(handler); };
  }, [filters.searchTerm, clientApprovalFilters.searchTerm, activeTab]);

  useEffect(() => {
    if (user && user.id) {
        fetchData();
    } else {
        setLoading(false);
        setError("ユーザー情報が見つかりません。");
    }
  }, [fetchData, user?.id]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // ★ 修正: タブ切り替え時のリセットも初期設定に合わせて変更（必要に応じて 'all' に戻す場合はここを調整してください）
    setFilters({ status: 'all', reviewed: 'unreviewed', searchTerm: '' });
    setClientApprovalFilters({ status: 'pending_approval', searchTerm: '' });
    setDebouncedSearchTerm('');
    setSortBy({ column: 'updated_at', direction: 'desc' });
  };
  
  const handleSort = (column) => {
    setSortBy(prev => ({ column, direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc' }));
  };

  const StatusBadge = ({ status }) => {
    const normalizedStatus = status ?? '__NULL__';
    const label = REVIEW_STATUS_LABELS[normalizedStatus] || {
      approved: '承認済み',
      rejected: '却下済み',
    }[normalizedStatus] || normalizedStatus;
    return (<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${REVIEW_STATUS_STYLES[normalizedStatus] || REVIEW_STATUS_STYLES.DEFAULT}`}>{label}</span>);
  };

  const PriorityBadge = ({ score }) => {
    const numeric = Number(score || 0);
    const tone = numeric >= 80
      ? 'bg-red-100 text-red-800'
      : numeric >= 50
        ? 'bg-orange-100 text-orange-800'
        : numeric >= 25
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-gray-100 text-gray-700';
    return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tone}`}>優先 {numeric}</span>;
  };
  
  const handleFilterChange = (filterName, value) => {
    const updater = (activeTab === 'admin' || activeTab === 'client_review') ? setFilters : setClientApprovalFilters;
    updater(prev => ({ ...prev, [filterName]: value }));
  };
  
  const currentFilters = activeTab === 'admin' || activeTab === 'client_review' ? filters : clientApprovalFilters;
  const tableColSpan = (activeTab === 'admin' || activeTab === 'client_review') ? 6 : 5;

  if (error) return <div className="p-8 text-center text-red-500">エラー: {error}</div>;

  return (
    <div className="bg-gray-50 min-h-screen p-3 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">フォーム解析レビュー</h1>
          <p className="mt-2 text-sm text-gray-600">
            {isClientView 
              ? "フォーム送信が失敗した案件を確認し、フォーム解析精度と送信可否判定の品質改善にご協力ください。ご協力に応じて特典が付与されます。"
              : "VPSの自動処理結果のレビュー、クライアントからのレビュー依頼の承認を行います。"}
          </p>
        </header>
        
        {/* ★ 修正点: クライアントビューの場合、ゲーミフィケーションカードを表示 */}
        {isClientView && <RewardProgressCard privileges={userPrivileges} />}

        {/* ★ 修正点: 管理者ビューの場合のみ、タブとサマリーカードを表示 */}
        {!isClientView && (
          <>
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Tabs">
                <button
                  onClick={() => handleTabChange('admin')}
                  className={`${activeTab === 'admin' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  管理者レビュー
                </button>
                <button
                  onClick={() => handleTabChange('client_approval')}
                  className={`${activeTab === 'client_approval' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  クライアント承認待ち
                </button>
              </nav>
            </div>
            {activeTab === 'admin' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <SummaryCard title="未レビュー案件" value={loading ? '...' : reviewData.summary.unreviewed} icon={<AlertCircleIcon className="w-6 h-6 text-yellow-500"/>} colorClass="border-yellow-500" />
                  <SummaryCard title="レビュー済み案件" value={loading ? '...' : reviewData.summary.reviewed} icon={<CheckCircleIcon className="w-6 h-6 text-green-500"/>} colorClass="border-green-500" />
                  <SummaryCard title="全確認データ" value={loading ? '...' : reviewData.summary.total} icon={<BrainCircuitIcon className="w-6 h-6 text-indigo-500"/>} colorClass="border-indigo-500" />
                </div>
            )}
          </>
        )}


        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-grow w-full sm:w-auto">
              <SearchIcon />
              <input 
                type="text" 
                placeholder="ホスト名やクライアントIDで検索..." 
                value={currentFilters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)} 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {(activeTab === 'admin' || activeTab === 'client_review') && (
                <div className="grid grid-cols-1 gap-3 w-full sm:w-auto sm:flex sm:items-center sm:gap-4">
                  <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full sm:w-48 appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-base focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                    {REVIEW_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  {!isClientView && (
                    <select value={filters.reviewed} onChange={(e) => handleFilterChange('reviewed', e.target.value)} className="w-full sm:w-48 appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-base focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                      <option value="all">すべてのレビュー状況</option>
                      <option value="unreviewed">未レビュー</option>
                      <option value="reviewed">レビュー済み</option>
                    </select>
                  )}
                </div>
            )}
            {activeTab === 'client_approval' && (
                 <div className="flex items-center gap-4 w-full sm:w-auto">
                    <select value={clientApprovalFilters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full sm:w-48 appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-base focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                        <option value="pending_approval">承認待ち</option>
                        <option value="approved">承認済み</option>
                        <option value="rejected">却下済み</option>
                        <option value="all">すべて表示</option>
                    </select>
                 </div>
            )}
          </div>

          <div className="divide-y divide-gray-100 md:hidden">
            {loading ? (
              <div className="py-12 text-center text-gray-500">読み込み中...</div>
            ) : reviewData.items.length === 0 ? (
              <div className="py-12 text-center text-gray-500">対象の案件はありません。</div>
            ) : (
              reviewData.items.map((item) => (
                <div key={`${item.id}-mobile`} className="p-4">
                  {activeTab === 'admin' || activeTab === 'client_review' ? (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="break-words text-sm font-bold text-gray-900">{item.hostname}</p>
                          <a href={item.contact_url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-xs text-indigo-600">{item.contact_url}</a>
                        </div>
                        <StatusBadge status={item.status} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <PriorityBadge score={item.priority_score} />
                        {item.flow_type && <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{humanizeFlowType(item.flow_type)}</span>}
                        {Number(item.has_snapshot) === 1 && <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">証拠あり</span>}
                        {Number(item.review_surface_cluster_size || 0) > 1 && <span className="px-2 py-0.5 text-xs rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">類似 {item.review_surface_cluster_size}件</span>}
                      </div>
                      {item.review_focus && <p className="mt-2 text-xs text-gray-500">{item.review_focus}</p>}
                      {item.review_surface_summary && <p className="mt-2 text-xs text-slate-600 line-clamp-2">{humanizeReviewSurfaceSummary(item.review_surface_summary)}</p>}
                      {item.last_failure_reason && <p className="mt-2 text-xs text-gray-500 line-clamp-2">{item.last_failure_reason}</p>}
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                        {!isClientView && <p><span className="font-semibold">レビュー状況</span><br />{item.is_human_reviewed ? <span className="text-green-600 font-semibold">レビュー済み</span> : <span className="text-yellow-600">未レビュー</span>}</p>}
                        <p><span className="font-semibold">最終更新</span><br />{new Date(item.updated_at).toLocaleString('ja-JP')}</p>
                      </div>
                      <button onClick={() => navigate(`/admin/review-detail/${item.id}`)} className="mt-4 w-full rounded-md bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600">
                        レビューする
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="break-words text-sm font-bold text-gray-900">{item.hostname}</p>
                          <a href={item.contact_url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-xs text-indigo-600">{item.contact_url}</a>
                        </div>
                        <StatusBadge status={item.status} />
                      </div>
                      <div className="mt-3 space-y-1 text-xs text-gray-500">
                        <p><span className="font-semibold">提出日時:</span> {new Date(item.created_at).toLocaleString('ja-JP')}</p>
                        <p className="break-all"><span className="font-semibold">クライアントID:</span> {item.client_id}</p>
                      </div>
                      <button onClick={() => navigate(`/admin/client-review-detail/${item.id}`)} className="mt-4 w-full rounded-md bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600">
                        詳細を確認
                      </button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {activeTab === 'admin' || activeTab === 'client_review' ? (
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">優先度（参考）</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><button onClick={() => handleSort('status')} className="flex items-center">VPS判断 <ChevronsUpDownIcon /></button></th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><button onClick={() => handleSort('hostname')} className="flex items-center">ホスト名 <ChevronsUpDownIcon /></button></th>
                      {!isClientView && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">レビュー状況</th>}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell"><button onClick={() => handleSort('updated_at')} className="flex items-center">最終更新日時 <ChevronsUpDownIcon /></button></th>
                      <th scope="col" className="relative px-6 py-3"><span className="sr-only">アクション</span></th>
                    </tr>
                ) : (
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">提出日時</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">クライアントID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">対象サイト</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                        <th scope="col" className="relative px-6 py-3"><span className="sr-only">アクション</span></th>
                    </tr>
                )}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                    <tr><td colSpan={tableColSpan} className="text-center py-12 text-gray-500">読み込み中...</td></tr>
                ) : reviewData.items.length === 0 ? (
                    <tr><td colSpan={tableColSpan} className="text-center py-12 text-gray-500">対象の案件はありません。</td></tr>
                ) : (
                    reviewData.items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        {activeTab === 'admin' || activeTab === 'client_review' ? (
                            <>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-2">
                                      <PriorityBadge score={item.priority_score} />
                                      {item.review_focus && <span className="text-xs text-gray-500">{item.review_focus}</span>}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={item.status} /></td>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{item.hostname}</div>
                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                      {item.flow_type && <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{humanizeFlowType(item.flow_type)}</span>}
                                      {Number(item.has_snapshot) === 1 && <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">証拠あり</span>}
                                      {Number(item.review_surface_cluster_size || 0) > 1 && <span className="px-2 py-0.5 text-xs rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">類似 {item.review_surface_cluster_size}件</span>}
                                      {Number(item.choice_option_count || 0) > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-sky-50 text-sky-700 border border-sky-100">選択肢 {item.choice_option_count}</span>}
                                      {Number(item.custom_input_count || 0) > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">固定入力 {item.custom_input_count}</span>}
                                      {Number(item.field_mapping_count || 0) > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 border border-gray-200">項目 {item.field_mapping_count}</span>}
                                      {item.final_submit_selector && <span className="px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-100">最終送信ヒントあり</span>}
                                      {item.step_advance_selector && <span className="px-2 py-0.5 text-xs rounded-full bg-violet-50 text-violet-700 border border-violet-100">次ステップヒントあり</span>}
                                    </div>
                                    {item.review_surface_summary && (
                                      <p className="mt-2 text-xs text-slate-600 line-clamp-2 max-w-xl">{humanizeReviewSurfaceSummary(item.review_surface_summary)}</p>
                                    )}
                                    {item.last_failure_reason && (
                                      <p className="mt-2 text-xs text-gray-500 line-clamp-2 max-w-xl">{item.last_failure_reason}</p>
                                    )}
                                    <a href={item.contact_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-indigo-600 truncate max-w-xs block">{item.contact_url}</a>
                                </td>
                                {!isClientView && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{item.is_human_reviewed ? (<span className="text-green-600 font-semibold">レビュー済み</span>) : (<span className="text-yellow-600">未レビュー</span>)}</td>}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">{new Date(item.updated_at).toLocaleString('ja-JP')}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button onClick={() => navigate(`/admin/review-detail/${item.id}`)} className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-md transition">レビューする</button>
                                </td>
                            </>
                        ) : (
                            <>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(item.created_at).toLocaleString('ja-JP')}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">{item.client_id}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <p className="text-sm font-medium text-gray-900">{item.hostname}</p>
                                    <a href={item.contact_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline truncate max-w-xs block">{item.contact_url}</a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={item.status} /></td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button onClick={() => navigate(`/admin/client-review-detail/${item.id}`)} className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-md transition">詳細を確認</button>
                                </td>
                            </>
                        )}
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={reviewData.pagination.currentPage || 1} totalPages={reviewData.pagination.totalPages || 1} onPageChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default ReviewDashboard;
